import React from 'react';
import { Button, Tooltip, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { FctEstado, fmtFecha } from '../../../fct/react';
import { fct } from '../../../fct/tokens/tokens';
import { TitularidadImplicada } from '../../../api/clientesDuplicadosApi';
import { BuscadorCliente } from './BuscadorCliente';
import { InformeFusionView } from './InformeFusionView';
import { NotaProceso } from './NotaProceso';
import { cambiosDeReglas, contarVerificacion, resumirEdicion } from './reglas';
import { AsistenteDuplicados } from './useAsistenteDuplicados';

const { Text } = Typography;

const fecha = (v: string | null) => (v ? fmtFecha(v) : '—');

// ─── Paso 1 · Selección ─────────────────────────────────────────

export const PasoSeleccion: React.FC<{ a: AsistenteDuplicados }> = ({ a }) => (
  <Space direction="vertical" size={12} style={{ width: '100%' }}>
    <Text type="secondary" style={{ fontSize: 12 }}>
      Busca por nombre o por código las dos fichas que quieres enfrentar. La asignación
      izquierda/derecha es provisional: la decide la regla en el paso 3.
    </Text>
    <BuscadorCliente
      etiqueta="Cliente 1 — nombre o código"
      valor={a.candidatoA}
      excluir={a.candidatoB?.idCliente}
      disabled={a.irreversibleIniciado}
      onElegir={a.setCandidatoA}
    />
    <BuscadorCliente
      etiqueta="Cliente 2 — nombre o código"
      valor={a.candidatoB}
      excluir={a.candidatoA?.idCliente}
      disabled={a.irreversibleIniciado}
      onElegir={a.setCandidatoB}
    />
    {a.cargandoComparacion && <Text type="secondary" style={{ fontSize: 12 }}>Leyendo las dos fichas…</Text>}
    {a.errorComparacion && (
      <NotaProceso tipo="error" titulo={a.errorComparacion.titulo ?? 'No se han podido enfrentar las fichas'} detalle={a.errorComparacion.mensaje} />
    )}
  </Space>
);

// ─── Paso 2 · Verificación ──────────────────────────────────────

export const PasoVerificacion: React.FC<{ a: AsistenteDuplicados }> = ({ a }) => {
  if (!a.comparacion) return null;
  const c = contarVerificacion(a.comparacion.campos);
  const alarmas = a.comparacion.campos.filter((k) => k.alarmaActiva);
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Text style={{ fontSize: 12 }}>
        <Text strong>{c.coinciden}</Text> campos coinciden · <Text strong>{c.difieren}</Text> difieren ·{' '}
        <Text strong>{c.soloUno}</Text> solo en una ficha ·{' '}
        <Text strong style={{ color: c.alarmas ? fct.estado.riesgo.texto : undefined }}>{c.alarmas}</Text> señales de alarma
      </Text>
      {c.clavesCoinciden > 0 && (
        <NotaProceso
          tipo="ok"
          titulo={`Comparten ${c.etiquetasClaveCoincide.join(', ')}`}
          detalle="Compartir una clave externa es prueba fuerte de que son la misma persona."
        />
      )}
      {alarmas.length > 0 && (
        <NotaProceso
          tipo="error"
          titulo={`Puede que no sean la misma persona: difieren en ${alarmas.map((k) => k.etiqueta.toLowerCase()).join(' y ')}`}
          detalle="¿Son la misma persona? Pueden ser familiares (padre e hijo, hermanos) o compartir apellidos. Si no son la misma persona, no continúes."
        />
      )}
      {c.avisosClave > 0 && (
        <NotaProceso tipo="aviso" titulo="Identificadores externos distintos" detalle="Revisar los id_colegiado_CU2 e ID_Web antes de decidir. No se puede eliminar ningún cliente que tenga id_colegiado_CU2." />
      )}
      {c.textoSucio > 0 && (
        <NotaProceso tipo="info" titulo={`${c.textoSucio} campo${c.textoSucio === 1 ? '' : 's'} con texto sucio`} detalle="Espacios sobrantes o caracteres rotos: se normalizará al fusionar." />
      )}
      <Space wrap>
        {/* Solo icono: el tooltip y el aria-label dicen qué hace. */}
        <Tooltip title="No son la misma persona: empezar de nuevo">
          <Button danger icon={<ArrowLeftOutlined />} onClick={a.reiniciar} aria-label="No son la misma persona" />
        </Tooltip>
        <Button type="primary" onClick={a.avanzar}>
          Continuar <CheckOutlined /><ArrowRightOutlined style={{ marginLeft: 2 }} />
        </Button>
      </Space>
    </Space>
  );
};

// ─── Paso 3 · Superviviente ─────────────────────────────────────

const columnasTitularidad = [
  { title: 'Cliente', dataIndex: 'idCliente', key: 'c', width: 80, className: 'fct-mono' },
  {
    title: 'Farmacia',
    key: 'f',
    render: (_: unknown, t: TitularidadImplicada) => (
      <Space direction="vertical" size={0}>
        <Text>{t.farmacia ?? `Entidad ${t.idEntidad}`}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          #{t.codigoFarmacia ?? t.idEntidad}{t.ubicacion ? ` · ${t.ubicacion}` : ''}
        </Text>
      </Space>
    ),
  },
  { title: 'Desde', dataIndex: 'fechaInicio', key: 'd', width: 90, className: 'fct-num', align: 'right' as const, render: fecha },
  { title: 'Hasta', dataIndex: 'fechaFin', key: 'h', width: 90, className: 'fct-num', align: 'right' as const, render: (v: string | null) => (v ? fecha(v) : <FctEstado personalizado={{ etiqueta: 'vigente', familia: 'aviso' }} />) },
  { title: 'Id titularidad', dataIndex: 'idRelacion', key: 'r', width: 110, className: 'fct-mono' },
];

export const PasoSuperviviente: React.FC<{ a: AsistenteDuplicados; recargando: boolean; onRecargar: () => void }> = ({ a, recargando, onRecargar }) => {
  const c = a.comparacion;
  if (!c) return null;
  const { regla } = c;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {regla.bloqueo ? (
        <NotaProceso
          tipo="error"
          abierta
          titulo="Bloqueo: no se puede decidir qué cliente se conserva"
          detalle={
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <span>{regla.explicacion}</span>
              <span>
                Resuélvelo en Gescom, fuera de esta herramienta (eliminando una titularidad o deshaciendo
                una vinculación), y pulsa el icono de recargar entre las dos columnas. Este bloqueo no se puede saltar.
              </span>
              {regla.resultado === 'BLOQUEO_CU2' && (
                <span>Vinculaciones CU2: {c.fichas.map((f) => `${f.codigo ?? f.idCliente} → ${f.idColegiadoCu2}`).join(' · ')}</span>
              )}
              {c.titularidades.length > 0 && (
                <Table<TitularidadImplicada>
                  size="small"
                  pagination={false}
                  rowKey={(t) => `${t.idCliente}-${t.idRelacion ?? t.idEntidad}`}
                  columns={columnasTitularidad}
                  dataSource={c.titularidades}
                />
              )}
              <Button size="small" icon={<ReloadOutlined />} loading={recargando} onClick={onRecargar}>Recargar los dos</Button>
            </Space>
          }
        />
      ) : (
        <NotaProceso
          tipo={regla.forzado ? 'info' : 'aviso'}
          titulo={regla.forzado ? 'La regla decide qué cliente se conserva' : 'Decides tú qué cliente se conserva'}
          detalle={
            <>
              {regla.explicacion}
              {regla.forzado && ' Intercambiar está deshabilitado: eliminar el otro rompería la vinculación.'}
            </>
          }
        />
      )}

      {(regla.avisoCu2SinPoblar || regla.avisoFarmaciaSinPoblar) && (
        <NotaProceso
          tipo="aviso"
          titulo="Señal sin comprobar"
          detalle={[
            regla.avisoCu2SinPoblar && 'Ninguna ficha de la base tiene CU2 informado: la protección de la ficha vinculada a CU2 no se ha podido aplicar.',
            regla.avisoFarmaciaSinPoblar && 'No hay titularidades vigentes en la base: la regla de farmacia no se ha podido aplicar.',
          ].filter(Boolean).join(' ')}
        />
      )}

      {/* Solo los dos códigos (Dev, 2026-09-24): los datos ya están en la rejilla. Verde el que se
          conserva, rojo el que se elimina; con bloqueo todavía no hay ni uno ni otro. */}
      {(() => {
        const [primera, segunda] = [...c.fichas].sort((x, y) => x.lado - y.lado);
        const chip = (f: typeof primera) => (
          <Tag
            color={regla.bloqueo ? undefined : f.lado === 1 ? 'green' : 'red'}
            style={{ margin: 0, fontSize: 15, lineHeight: '24px', padding: '0 10px' }}
          >
            {regla.bloqueo ? '' : f.lado === 1 ? 'Conservar ' : 'Eliminar '}{f.codigo ?? `ID ${f.idCliente}`}
          </Tag>
        );
        // El mismo intercambio que el icono entre las dos columnas de la rejilla (Dev, 2026-09-24).
        const motivo = a.puedeIntercambiar ? 'Intercambiar'
          : regla.bloqueo ? 'Con bloqueo no se puede intercambiar'
            : regla.forzado ? 'La regla fija qué cliente se conserva: no se puede intercambiar'
              : 'No se puede intercambiar';
        return (
          <Space size={4} align="center" wrap>
            {chip(primera)}
            <Tooltip title={motivo}>
              <Button
                size="small"
                type={a.intercambiada ? 'default' : 'text'}
                icon={<SwapOutlined />}
                disabled={!a.puedeIntercambiar}
                onClick={a.intercambiar}
                aria-label="Intercambiar"
              />
            </Tooltip>
            {chip(segunda)}
          </Space>
        );
      })()}

      <Button type="primary" icon={<CheckOutlined />} disabled={regla.bloqueo} onClick={a.avanzar} style={{ alignSelf: 'flex-start' }}>
        Confirmar
      </Button>
    </Space>
  );
};

// ─── Paso 4 · Fusión de datos ───────────────────────────────────

export const PasoFusion: React.FC<{ a: AsistenteDuplicados }> = ({ a }) => {
  if (!a.comparacion) return null;
  const r = resumirEdicion(a.edicion);
  const pendientesReglas = cambiosDeReglas(a.comparacion, a.edicion);
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Text style={{ fontSize: 12 }}>
        {r.sinCambios
          ? 'Sin cambios: queda la ficha del superviviente tal como está.'
          : <><Text strong>{r.camposTocados}</Text> campos y <Text strong>{r.elementosTocados}</Text> elementos de lista cambiados en la ficha que se conserva.</>}
      </Text>
      {pendientesReglas > 0 && (
        <NotaProceso
          tipo="aviso"
          titulo={`Las reglas tomarían ${pendientesReglas} dato${pendientesReglas === 1 ? '' : 's'} más del duplicado`}
          detalle="Si no los pasas a la izquierda, se perderán al eliminar el duplicado. Pásalos uno a uno con su flecha o todos con «Aplicar reglas de dato ganador»."
        />
      )}
      <NotaProceso
        tipo="info"
        titulo="Cómo se decide lo que queda"
        detalle={
          <>
            Cada columna empieza con los datos de su cliente, y la izquierda es lo que quedará. La flecha ←
            pasa un dato de la derecha; «Aplicar reglas de dato ganador», encima de las columnas, pasa de
            una vez lo que dicen las reglas, sin tocar lo que ya hayas decidido. También puedes escribir encima.
            Todo lo cambiado tiene su flecha curvada para recuperar el original. En emails y teléfonos, la
            estrella marca el principal y la cruz quita. Identificadores, calculados y farmacia no se editan.
          </>
        }
      />
      {a.errorCatalogos && (
        <NotaProceso tipo="aviso" titulo="No se han podido cargar los desplegables" detalle="Los campos de selección se muestran, pero no se pueden cambiar. Recarga la página." />
      )}
      <Space wrap>
        <Button size="small" onClick={a.restaurarTodo} disabled={r.sinCambios}>Recuperar los originales</Button>
        <Button type="primary" icon={<CheckOutlined />} onClick={a.avanzar}>Continuar</Button>
      </Space>
    </Space>
  );
};

// ─── Paso 6 · Cierre ────────────────────────────────────────────

export const PasoCierre: React.FC<{ a: AsistenteDuplicados; onVerInforme: () => void }> = ({ a, onVerInforme }) => {
  const e = a.ejecucion;
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <NotaProceso
        tipo="ok"
        titulo={`Fusión nº ${e.idFusion} completada: ${e.aplicado?.codigoEliminado ?? ''} eliminado`}
        detalle={<InformeFusionView lineas={e.lineas} />}
      />
      <Space wrap>
        <Button type="primary" onClick={a.reiniciar}>Tratar otro duplicado</Button>
        <Button size="small" onClick={onVerInforme}>Ver informe nº {e.idFusion}</Button>
      </Space>
    </Space>
  );
};
